import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { keyBy, orderBy } from 'lodash';
import {
  Text,
  Card,
  Flex,
  Box,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import { Row, Table } from '@tanstack/react-table';
import { Doughnut } from 'react-chartjs-2';
import { TState } from '../../redux/store';
import { allIcons, NormalIcon } from '../utils/Icon';
import { isEmpty } from '../../helpers';
import { useCanI } from '../../redux/actions';
import { TTab } from '../tabs/type';
import DoughnutWithLegend from '../stats/DoughnutWithLegend';

type TKPI = {
  _id: string;
  field: string;
  type?: string; // 'avg' | 'aggregate' | 'group';
  value?: string | number;
  values?: (string | number)[];
  rows: Row<any>[];
  isBig?: boolean;
};

const KPI = ({ _id, field, type, value, values, rows, isBig }: TKPI) => {
  const dataInputs = useSelector((state: TState) => state.dataInputs);
  const userList = useSelector((state: TState) => state.userList);
  const theme = useSelector((state: TState) => state.theme);

  const canI = useCanI();
  const show = canI(field, 'read');

  if (!show) {
    return null;
  }

  const dataInputsByName = keyBy(dataInputs, 'name_id');
  const dataInput =
    dataInputsByName[field.replace(/_city|_postal|_dept/gi, '')];

  const userListById = keyBy(userList, '_id');

  const nbRows = rows.length;

  const isNbRows = field === 'nbrows';
  const isNumber = /number/.test(dataInput?.type);
  const isSelect = /select/.test(dataInput?.type);
  const isSwitch = /switch/.test(dataInput?.type);
  const isMulti = /multi/.test(dataInput?.type);
  const isUser = /users/.test(dataInput?.type);
  const isPostal = /_postal/.test(field);

  const avgValue =
    type === 'avg' &&
    !isNaN(Number(value)) &&
    (
      (rows.reduce(
        (sum, row: any) =>
          Number(row.original[field]) === Number(value) ? sum + 1 : sum,
        0,
      ) /
        nbRows) *
      100
    ).toFixed(0);

  let chartData =
    type && /avg|group/.test(type) && (!value || values?.length)
      ? rows.reduce((prev, curr) => {
          const rowValue = curr.original[field];

          if (isEmpty(rowValue) && !isSwitch) return prev;

          const newVal = [...prev];

          for (const rValue of isMulti ? rowValue : [rowValue]) {
            if (
              (isEmpty(rValue) && !isSwitch) ||
              rValue < 0 ||
              (values?.length && !values.includes(`${rValue}`))
            )
              continue;

            const label = isSwitch
              ? rValue
                ? 'Oui'
                : 'Non'
              : isSelect
              ? dataInput.options?.[Number(rValue ?? undefined)] ?? 'VIDE'
              : isUser
              ? userListById[rValue]?.name ?? 'VIDE'
              : rValue ?? 'VIDE';

            const color = isSelect
              ? dataInput.colors?.[Number(rValue ?? undefined)] ?? '#ffffff'
              : isUser
              ? userListById[rValue]?.userColor ?? '#ffffff'
              : '';

            const index = prev.map((p) => p.label).indexOf(label) ?? -1;

            if (index < 0) {
              newVal.push({ label, color, count: 1 });
            } else {
              newVal[index].count++;
            }
          }

          return newVal;
        }, [] as { label: string; color: string; count: number }[])
      : null;

  chartData = chartData && orderBy(chartData, (cd) => cd.label);

  return !dataInput && !isNbRows ? null : isBig && !!chartData ? (
    <Flex boxSize='100%' justifyContent='center' alignItems='center'>
      <DoughnutWithLegend
        data={{
          labels: chartData.map((cd) => cd.label),
          datasets: [
            {
              data: chartData.map((cd) =>
                Number(
                  type === 'avg'
                    ? ((cd.count / nbRows) * 100).toFixed(2)
                    : cd.count,
                ),
              ),
              backgroundColor: chartData.map((cd) => cd.color),
            },
          ],
        }}
      />
    </Flex>
  ) : (
    <Card variant='elevated' direction='row' p={3} alignItems='center'>
      <Flex direction='column' flexGrow={1} minW='50px' mr={3}>
        <Text color={theme.secondary} fontSize={theme.fontSize.small}>
          {isNbRows
            ? 'Nombre de lignes'
            : `${
                isPostal ? 'Code postal ' : ''
              }${dataInput?.label.toUpperCase()}`}
          {type === 'avg' &&
            value &&
            ` - ${dataInput.options?.[Number(value)]}`}
        </Text>
        <Text flexGrow={1} fontWeight='bold' fontSize={theme.fontSize.bigger}>
          {isNbRows && nbRows}
          {isNumber &&
            rows
              .reduce(
                (sum, row: any) =>
                  isNaN(row.original[field])
                    ? sum
                    : sum + Number(row.original[field]),
                0,
              )
              .toFixed(0)}
          {isNumber && dataInput?.unity && ` ${dataInput?.unity}`}
          {avgValue && isNaN(Number(avgValue)) ? 0 : avgValue}
          {!!avgValue && '%'}
          {!!chartData && (
            <span
              className={`tooltip-text-${field}-${_id}`}
              style={{ fontWeight: 'bold', fontSize: theme.fontSize.small }}
            />
          )}
        </Text>
      </Flex>
      {!chartData && (
        <NormalIcon
          as={
            isNbRows
              ? allIcons.HiListBullet
              : type === 'avg'
              ? allIcons.HiChartPie
              : allIcons.HiCircleStack
          }
          boxSize='27px'
        />
      )}
      {!!chartData && (
        <Box w='50px'>
          <Doughnut
            options={{
              plugins: {
                datalabels: {
                  display: false,
                },
                tooltip: {
                  enabled: false,
                  external: function (context) {
                    const tooltip = context.tooltip;
                    const span = document.getElementsByClassName(
                      `tooltip-text-${field}-${_id}`,
                    )[0] as HTMLElement;

                    if (!span) return;

                    if (tooltip.opacity === 0) {
                      span.innerText = '';
                      span.style.display = 'none';
                      return;
                    }

                    if (tooltip.dataPoints) {
                      const point = tooltip.dataPoints[0];
                      const label = point.label;
                      const value = point.formattedValue;
                      span.innerText = `${label} - ${value}${
                        type === 'avg' ? '%' : ''
                      }`;
                      span.style.display = 'block';
                    }

                    if (
                      span.getBoundingClientRect().left <
                      window.innerWidth / 2
                    ) {
                      span.style.right = '';
                      span.style.left = '5px';
                    } else {
                      span.style.left = '';
                      span.style.right = '0px';
                    }
                  },
                },
                legend: {
                  display: false,
                },
              },
            }}
            data={{
              labels: chartData.map((cd) => cd.label),
              datasets: [
                {
                  data: chartData.map((cd) =>
                    type === 'avg'
                      ? ((cd.count / nbRows) * 100).toFixed(2)
                      : cd.count,
                  ),
                  backgroundColor: chartData.map((cd) => cd.color),
                  // hoverOffset: 4,
                },
              ],
            }}
          />
        </Box>
      )}
    </Card>
  );
};

const KPIs = ({
  tableInstance,
  currTab,
  rows,
}: {
  tableInstance: Table<any>;
  currTab: TTab;
  rows: any;
}) => {
  const isMobile = useSelector((state: TState) => state.isMobile);
  const dataInputs = useSelector((state: TState) => state.dataInputs);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [currentKpiProps, setCurrentKpiProps] = useState({} as TKPI);
  const [title, setTitle] = useState('');

  const render = useCallback(
    () =>
      currTab?.KPIs?.length ? (
        <>
          <Flex
            gap={2}
            flexWrap='wrap'
            {...(isMobile
              ? { maxH: '160px', pb: '20px', mb: '-20px', overflow: 'auto' }
              : {})}
          >
            {currTab?.KPIs?.map((kpi) => {
              const { value, type, values, field } = kpi;

              const hasChart =
                type && /avg|group/.test(type) && (!value || values?.length);

              const dataInputsByName = keyBy(dataInputs, 'name_id');
              const dataInput =
                dataInputsByName[field.replace(/_city|_postal|_dept/gi, '')];

              return (
                <Box
                  cursor={hasChart ? 'pointer' : 'default'}
                  onClick={() => {
                    if (hasChart) {
                      setTitle(
                        `${dataInput?.label.toUpperCase()}${
                          type === 'avg' && value
                            ? ` - ${dataInput.options?.[Number(value)]}`
                            : ''
                        }`,
                      );

                      setCurrentKpiProps({ ...kpi, rows });
                      onOpen();
                    }
                  }}
                >
                  <KPI {...kpi} rows={rows} />
                </Box>
              );
            })}
          </Flex>
          <Modal isOpen={isOpen} onClose={onClose} size='3xl' isCentered>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>{title}</ModalHeader>
              <ModalCloseButton onClick={onClose} />
              <ModalBody m={5}>
                <KPI {...currentKpiProps} isBig />
              </ModalBody>
            </ModalContent>
          </Modal>
        </>
      ) : null,
    [tableInstance.getFilteredRowModel(), isOpen],
  );

  return render();
};

export default KPIs;
